import React, { useState } from 'react';
import { aiAPI } from '../../utils/api';
import { X, Loader2, Sparkles, Check, AlertCircle } from 'lucide-react';

function EnrichField({ label, value, isNew }) {
  if (!value) return null;
  return (
    <div className="flex items-start justify-between py-2 border-b border-slate-100">
      <div><p className="text-sm font-medium text-slate-700">{label}</p><p className="text-sm text-slate-900">{value}</p></div>
      {isNew && <span className="text-xs px-1.5 py-0.5 bg-green-100 text-green-700 rounded">New</span>}
    </div>
  );
}

function AIEnrichModal({ isOpen, onClose, client, onEnrichmentApplied }) {
  var s1 = useState(false); var loading = s1[0]; var setLoading = s1[1];
  var s2 = useState(null); var enrichment = s2[0]; var setEnrichment = s2[1];
  var s3 = useState(false); var applying = s3[0]; var setApplying = s3[1];
  var s4 = useState(false); var applied = s4[0]; var setApplied = s4[1];
  var s5 = useState(null); var error = s5[0]; var setError = s5[1];

  if (!isOpen || !client) return null;

  var handleEnrich = async function() {
    setLoading(true); setError(null); setEnrichment(null); setApplied(false);
    try {
      var res = await aiAPI.enrichOrganization(client._id);
      setEnrichment(res.data.enrichment || {});
    } catch (err) {
      var msg = 'Enrichment failed.';
      if (err.response && err.response.data) msg = err.response.data.detail || msg;
      setError(msg);
    } finally { setLoading(false); }
  };

  var handleApply = async function() {
    if (!enrichment) return;
    setApplying(true);
    try {
      await aiAPI.applyEnrichment(client._id, { enrichment: enrichment });
      setApplied(true);
      if (onEnrichmentApplied) onEnrichmentApplied();
    } catch (err) { setError('Failed to apply enrichment'); }
    finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="ai-enrich-modal">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[85vh] overflow-y-auto" onClick={function(e) { e.stopPropagation(); }}>
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between z-10">
          <div><h2 className="text-lg font-semibold text-slate-900">AI Enrichment</h2><p className="text-sm text-slate-500">{client.client_name}</p></div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X size={24} /></button>
        </div>
        <div className="p-6 space-y-4">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center gap-2"><AlertCircle size={16} /> {error}</div>}

          {!enrichment && !loading && (
            <div className="text-center py-6">
              <Sparkles size={40} className="text-primary mx-auto mb-3" />
              <p className="text-slate-600 mb-1">Enrich <strong>{client.client_name}</strong> with AI</p>
              <p className="text-sm text-slate-400 mb-4">AI will analyze available data to fill in details.</p>
              <button onClick={handleEnrich} className="px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary/90 flex items-center justify-center gap-2 mx-auto" data-testid="ai-enrich-start-btn"><Sparkles size={18} /> Start Enrichment</button>
            </div>
          )}

          {loading && <div className="text-center py-12"><Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" /><p className="text-sm text-slate-600">Analyzing organization...</p></div>}

          {enrichment && !loading && <EnrichResults enrichment={enrichment} client={client} applied={applied} applying={applying} onReanalyze={handleEnrich} onApply={handleApply} />}
        </div>
      </div>
    </div>
  );
}

function EnrichResults({ enrichment, client, applied, applying, onReanalyze, onApply }) {
  return (
    <div className="space-y-4">
      <div className="bg-slate-50 rounded-lg p-4 space-y-1">
        <EnrichField label="Description" value={enrichment.description} isNew={enrichment.description !== client.notes} />
        <EnrichField label="Organization Type" value={enrichment.organization_type} isNew={enrichment.organization_type !== client.organization_type} />
        <EnrichField label="Sector" value={enrichment.sector} isNew={enrichment.sector !== client.sector} />
        <EnrichField label="Parent Entity" value={enrichment.parent_entity} isNew={enrichment.parent_entity !== client.parent_entity} />
        <EnrichField label="Company Size" value={enrichment.company_size} isNew={enrichment.company_size !== client.company_size} />
        <EnrichField label="Address" value={enrichment.address} isNew={enrichment.address !== client.address} />
        <EnrichField label="Suggested Priority" value={enrichment.suggested_priority} isNew={enrichment.suggested_priority !== client.priority} />
        <EnrichField label="Suggested Status" value={enrichment.suggested_status} isNew={enrichment.suggested_status !== client.prospecting_status} />
        <ServicesList services={enrichment.key_services} />
        {enrichment.notes && <div className="py-2"><p className="text-sm font-medium text-slate-700 mb-1">AI Notes</p><p className="text-sm text-slate-600">{enrichment.notes}</p></div>}
      </div>
      <div className="flex gap-3">
        <button onClick={onReanalyze} className="flex-1 py-2 border border-slate-300 rounded-lg text-sm text-slate-700 hover:bg-slate-50">Re-analyze</button>
        {applied ? (
          <div className="flex-1 py-2 bg-green-50 border border-green-200 rounded-lg text-sm text-green-700 text-center flex items-center justify-center gap-2"><Check size={16} /> Applied!</div>
        ) : (
          <button onClick={onApply} disabled={applying} className="flex-1 py-2 bg-primary text-white rounded-lg text-sm hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2" data-testid="ai-apply-enrichment-btn">
            {applying ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />} Apply to Client
          </button>
        )}
      </div>
    </div>
  );
}

function ServicesList({ services }) {
  if (!services || services.length === 0) return null;
  var tags = [];
  for (var i = 0; i < services.length; i++) {
    tags.push(<span key={i} className="text-xs px-2 py-0.5 bg-primary/10 text-primary rounded-full">{services[i]}</span>);
  }
  return (
    <div className="py-2 border-b border-slate-100">
      <p className="text-sm font-medium text-slate-700 mb-1">Key Services</p>
      <div className="flex flex-wrap gap-1">{tags}</div>
    </div>
  );
}

export default AIEnrichModal;
